/* eslint-disable react-hooks/rules-of-hooks */
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import { RenewalExposureUpdateRequest } from '../../../types/renewals';
import { getCharacteristic } from '../../../util/util';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getExposureName } from '../util_sharedEndorsementAndRenewal';
import AddExposureModal from './Exposures/AddExposureModal';
import { isExposureActive } from './util';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * The exposure detail page for an existing exposure on an renewal.
 */
const ExistingExposureDetails = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const classes = useStyles();
	const params = useParams();
	const { renewalData, authData } = props;
	const {
		renewalUpdateObj,
		productConfig,
		setLoading,
		policy,
		fields: form,
		saveFastForm,
	} = renewalData;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [removedExposure, setRemovedExposure] = useState(null);

	const selectedExposure = renewalData.selectedExposure as Exposure;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.name,
	);

	if (!exposureConfig) {
		return null;
	}

	// this is wrong since selectedExposure can be an ExposureCreateRequest which has no characteristics;
	const { characteristics } = selectedExposure;
	const exposureCharacteristicAtRenewal = getCharacteristic(
		characteristics,
		renewalUpdateObj.renewalStartTimestamp,
	);

	if (!exposureCharacteristicAtRenewal) {
		return null;
	}

	// Parent updateExposure array
	const updateExposuresArr = renewalUpdateObj.updateExposures;
	// exposureUpdateObj is the current ExposureUpdateRequest for the selected exposure
	let exposureUpdateObj = updateExposuresArr.find(
		(e) => e.exposureLocator === selectedExposure.locator,
	);
	exposureUpdateObj = exposureUpdateObj
		? exposureUpdateObj
		: ({} as RenewalExposureUpdateRequest);

	const isActive = isExposureActive(
		selectedExposure,
		policy,
		parseInt(renewalUpdateObj.renewalStartTimestamp, 10),
		[],
		[],
	);

	const removeExposure = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeExposure: { exposureLocator: selectedExposure.locator },
			});
			successToast('Successfully removed exposure');
			navigate(
				`/policy/${params.policyId}/renewals/${params.renewalId}/exposures`,
			);
		} catch (error) {
			errorToast('Error removing exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderRemoveExposureModal = () => {
		return (
			<ConfirmationModal
				entityName={
					(removedExposure as Exposure).name ||
					(removedExposure as ExposureCreateRequest).exposureName
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderRemoveExposureIcon = () => {
		return (
			<Tooltip title={'Remove ' + t('exposure.singular.upper')}>
				<IconButton
					aria-label='Remove'
					className={classes.removeIcon}
					onClick={() => openConfirmationModal(selectedExposure)}
				>
					<DeleteIcon />
				</IconButton>
			</Tooltip>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		setIsConfirmModalOpen(true);
		setRemovedExposure(exposure);
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				policyConfig={policyConfig}
				authData={authData}
				renewalData={renewalData}
			/>
		);
	};

	const defaultName = exposureConfig.displayName
		? exposureConfig.displayName
		: selectedExposure.name;
	const displayName = getExposureName(
		selectedExposure,
		defaultName,
		renewalUpdateObj,
		null,
	);
	const isEditable =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';
	return (
		<React.Fragment key={pathname}>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isEditable && renderRemoveExposureIcon()}
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</Box>
			<hr />
			<Grid container spacing={2}>
				<FastForm
					formName={selectedExposure.locator}
					form={form}
					configVersion={productConfig.configVersion}
					authData={props.authData}
					productName={productConfig.name}
					disabled={!isEditable}
					save={saveFastForm}
					setLoading={setLoading}
					disableExternalCalls={true}
					effectiveDate={renewalUpdateObj.overrideResourcesAsOfTimestamp}
				/>
			</Grid>
			{isEditable && renderActions()}
		</React.Fragment>
	);
};

export default withRenewalContext<Props>(ExistingExposureDetails);
